import { Card } from '../../../molecules/Card/Card';
import { isEmptyTotal, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import type { ChartSeriesToken } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { DonutChart } from '../../../organisms/DonutChart/DonutChart';
import { token } from '../../../tokens/tokens';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type ShareOfVoiceSlice = {
  /** The brand's option value. */
  key: string;
  label: string;
  color: ChartSeriesToken;
};

export type ShareOfVoiceView = {
  /** `Your share` (badge in points) and `Lead over next`. */
  kpis: KPIMetric[];
  /** One slice per brand with a mention, in chip order — the donut's and the legend's. */
  slices: ShareOfVoiceSlice[];
  /** One row per slice: `{ [nameKey]: label, mentions }`. */
  rows: ChartRow[];
  nameKey: string;
  /** Our share, or `—` when our brand is not in the category. */
  centerValue: string;
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type ShareOfVoiceCardProps = WidgetBlockProps & {
  view?: ShareOfVoiceView;
  /**
   * What the denominator is, which is the one thing that differs between the
   * card's two callers — and a difference the help text has to carry, because
   * a tooltip explaining the `Brands` chip on a screen that does not draw one
   * is worse than no tooltip. `chip` on Competitors, `all` on Overview.
   */
  category: 'chip' | 'all';
  /** Opens the conversations behind a slice (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Competitors' first card (931:36991), and Overview's (972:19386).
 *
 * **A cross-brand card** — the whole point is the field we stand in. On
 * Competitors the `Brands` chip is on screen, so share of voice is share of the
 * *selected* brands: deselect an operator and it leaves the denominator as well
 * as the plot, which keeps the slices summing to the figure in the middle. The
 * user can also deselect **us**, and every "our …" figure then reads an em dash
 * rather than a confident `0.0%`. On Overview no brand can be deselected, so
 * the em-dash branches simply never fire there.
 *
 * Two KPIs, not the three Figma draws: `Rank` was dropped on request
 * 2026-09-20 — a deliberate deviation; the donut already ranks the field by eye.
 */
export function ShareOfVoiceCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  category,
  onViewDetails,
}: ShareOfVoiceCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmptyTotal(view.rows, 'mentions');
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Share of voice"
      helpText={
        category === 'chip'
          ? 'Your share of all mentions across the brands currently selected.'
          : 'Your share of all mentions across every operator tracked.'
      }
      helpSupportingText={
        category === 'chip'
          ? 'The Brands chip is the category: deselect an operator and it leaves the denominator as well as the chart, so the slices always sum to the figure in the middle.'
          : 'The category is fixed here — every operator is in the denominator whatever the other chips do, so the slices always sum to the figure in the middle. Competitors is where the field can be narrowed.'
      }
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <DonutChart
            data={view.rows}
            onViewDetails={onViewDetails}
            nameKey={view.nameKey}
            valueKey="mentions"
            height={PLOT_HEIGHT}
            legend="none"
            centerLabel="your share of voice"
            centerValue={view.centerValue}
            colors={view.slices.map((s) => s.color)}
            formatValue={view.formatValue}
          />
        )
      }
      legend={
        view && <ChartLegend items={view.slices.map((s) => ({ label: s.label, color: token[s.color] }))} />
      }
    />
  );
}
